import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { tbqFunctions } from './firebase-config';

const PAGE_SIZE = 20;

const toDisplayTime = (changedAtMs) => {
  if (!changedAtMs) return '—';
  const date = new Date(changedAtMs);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const stringifySnapshot = (snapshot) => {
  try {
    return JSON.stringify(snapshot, null, 2);
  } catch (_) {
    return String(snapshot);
  }
};

const actorText = (actor) => {
  if (!actor) return 'Unknown actor';
  const name = actor.name || actor.email || actor.tbq_uid || actor.dtq_uid || 'Unknown actor';
  const extras = [actor.email, actor.tbq_uid].filter(Boolean).join(' | ');
  return extras ? `${name} (${extras})` : name;
};

const LocationHistoryModal = ({ location, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState({});
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const listDtqLocationHistory = useMemo(
    () => httpsCallable(tbqFunctions, 'listDtqLocationHistory'),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await listDtqLocationHistory({ locationId: location.id, pageSize: PAGE_SIZE });
        if (cancelled) return;
        const data = response?.data || {};
        setEvents(Array.isArray(data.events) ? data.events : []);
        setCursor(data.nextCursor || null);
        setHasMore(Boolean(data.hasMore));
      } catch (err) {
        console.error('Failed to load location history:', err);
        if (!cancelled) setError(`Failed to load history: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [location.id, listDtqLocationHistory]);

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    setError('');
    try {
      const response = await listDtqLocationHistory({
        locationId: location.id,
        pageSize: PAGE_SIZE,
        cursor,
      });
      const data = response?.data || {};
      setEvents((prev) => [...prev, ...(Array.isArray(data.events) ? data.events : [])]);
      setCursor(data.nextCursor || null);
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      console.error('Failed to load more history:', err);
      setError(`Failed to load more history: ${err.message}`);
    }
    setLoadingMore(false);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>Location History: {location.id}</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="history-body">
          {loading ? (
            <div className="loading-spinner">Loading history...</div>
          ) : events.length === 0 ? (
            <div className="history-empty">No history found for this location yet.</div>
          ) : (
            <div className="history-list">
              {events.map((event) => (
                <div key={event.id} className="history-item">
                  <div className="history-item-head">
                    <span className={`history-action history-${event.action || 'update'}`}>
                      {event.action || 'update'}
                    </span>
                    <span className="history-time">{toDisplayTime(event.changed_at_ms)}</span>
                  </div>
                  <div className="history-actor">{actorText(event.actor)}</div>
                  <div className="history-fields">
                    <strong>Changed fields:</strong>{' '}
                    {Array.isArray(event.changed_fields) && event.changed_fields.length
                      ? event.changed_fields.join(', ')
                      : '—'}
                  </div>
                  <button className="btn-history-expand" onClick={() => toggleExpanded(event.id)}>
                    {expandedIds[event.id] ? 'Hide before/after' : 'Show before/after'}
                  </button>
                  {expandedIds[event.id] ? (
                    <div className="history-snapshots">
                      <div>
                        <h4>Before</h4>
                        <pre>{stringifySnapshot(event.before)}</pre>
                      </div>
                      <div>
                        <h4>After</h4>
                        <pre>{stringifySnapshot(event.after)}</pre>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {!loading && hasMore ? (
            <div className="history-footer">
              <button className="btn-refresh" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load More'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LocationHistoryModal;
